import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import {
    FaThLarge, FaFolderOpen, FaSignOutAlt, FaExclamationTriangle,
    FaConciergeBell, FaShareAlt, FaEnvelopeOpenText, FaInfoCircle,
    FaLock, FaGavel, FaCookieBite, FaHeadset, FaFileInvoiceDollar,
    FaNewspaper, FaChevronDown, FaLayerGroup, FaComments, FaBook, FaShieldAlt,
    FaFilePdf, FaUndoAlt, FaUsers, FaCreditCard, FaTasks,
    FaBars, FaTimes,
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import logo from '../assets/logo.png';
import './Sidebar.css';

/* ─── MENU STRUCTURE ─── */
const MENU = [
    {
        id: 'overview',
        section: null,          // top-level items, no collapsible header
        items: [
            { label: 'Dashboard',        icon: <FaThLarge />,           to: '/admin/dashboard' },
            { label: 'Generate Invoice', icon: <FaFileInvoiceDollar />, to: '/admin/invoice'   },
            { label: 'Brochure',         icon: <FaFilePdf />,           to: '/admin/brochure'  },
            { label: 'Clients',          icon: <FaUsers />,             to: '/admin/clients'   },
            { label: 'Payments',         icon: <FaCreditCard />,        to: '/admin/payments'  },
            { label: 'Daily Tasks',      icon: <FaTasks />,             to: '/admin/tasks'     },
        ],
    },
    {
        id: 'content',
        section: 'Content',
        sectionIcon: <FaLayerGroup />,
        items: [
            { label: 'Services',   icon: <FaConciergeBell />, to: '/admin/services'  },
            { label: 'Portfolio',  icon: <FaFolderOpen />,    to: '/admin/portfolio' },
            { label: 'Blog Posts', icon: <FaNewspaper />,     to: '/admin/blog'      },
        ],
    },
    {
        id: 'comms',
        section: 'Communications',
        sectionIcon: <FaComments />,
        items: [
            { label: 'Inquiries',    icon: <FaEnvelopeOpenText />, to: '/admin/inquiries' },
            { label: 'Social Links', icon: <FaShareAlt />,         to: '/admin/socials'   },
        ],
    },
    {
        id: 'pages',
        section: 'Pages',
        sectionIcon: <FaBook />,
        items: [
            { label: 'About',        icon: <FaInfoCircle />, to: '/admin/about'         },
            { label: 'Contact Page', icon: <FaHeadset />,    to: '/admin/contact-page'  },
        ],
    },
    {
        id: 'legal',
        section: 'Legal',
        sectionIcon: <FaShieldAlt />,
        items: [
            { label: 'Privacy Policy',     icon: <FaLock />,       to: '/admin/privacy'        },
            { label: 'Terms & Conditions', icon: <FaGavel />,       to: '/admin/terms'          },
            { label: 'Cookie Policy',      icon: <FaCookieBite />,  to: '/admin/cookies'        },
            { label: 'Refund Policy',      icon: <FaUndoAlt />,     to: '/admin/refund-policy'  },
        ],
    },
];

/* ─── SUBMENU GROUP ─── */
const NavGroup = ({ group, isOpen, onToggle }) => {
    const location = useLocation();
    const isAnyActive = group.items.some(i => location.pathname === i.to);

    return (
        <div className={`nav-group ${isOpen ? 'nav-group--open' : ''}`}>
            {/* Category header */}
            <button
                className={`nav-group-header ${isAnyActive ? 'has-active' : ''}`}
                onClick={onToggle}
            >
                <span className="nav-group-icon">{group.sectionIcon}</span>
                <span className="nav-group-label">{group.section}</span>
                <motion.span
                    className="nav-group-arrow"
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.22 }}
                >
                    <FaChevronDown />
                </motion.span>
            </button>

            {/* Submenu items */}
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        className="nav-sublist"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                        <div className="nav-sublist-inner">
                            {group.items.map(item => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({ isActive }) =>
                                        `nav-subitem ${isActive ? 'active' : ''}`
                                    }
                                >
                                    <span className="nav-subitem-icon">{item.icon}</span>
                                    <span>{item.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

/* ─── SIDEBAR ─── */
const Sidebar = () => {
    const navigate  = useNavigate();
    const location  = useLocation();
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    /* Auto-open the section that contains the active route */
    const getDefaultOpen = () => {
        const active = {};
        MENU.forEach(g => {
            if (g.section && g.items.some(i => location.pathname === i.to)) {
                active[g.id] = true;
            }
        });
        return active;
    };

    const [openSections, setOpenSections] = useState(getDefaultOpen);

    /* Re-evaluate when route changes (e.g. after page load) */
    useEffect(() => {
        setOpenSections(prev => {
            const next = { ...prev };
            MENU.forEach(g => {
                if (g.section && g.items.some(i => location.pathname === i.to)) {
                    next[g.id] = true;
                }
            });
            return next;
        });
    }, [location.pathname]);

    const toggleSection = (id) =>
        setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));

    const handleLogout = () => {
        localStorage.removeItem('admin_token');
        toast.success('Logged out successfully');
        navigate('/admin/login');
    };

    return (
        <>
            {/* Floating Hamburger Toggle Button (mobile only) */}
            <button className="admin-hamburger-btn" onClick={() => setIsOpen(true)}>
                <FaBars />
            </button>

            {/* Backdrop Overlay (mobile only) */}
            {isOpen && (
                <div className="admin-sidebar-overlay" onClick={() => setIsOpen(false)} />
            )}

            <div className={`admin-sidebar ${isOpen ? 'sidebar-open' : ''}`}>
                {/* Brand */}
                <div className="sidebar-brand">
                    <img src={logo} alt="Nimai Admin" />
                    <span>Admin Panel</span>
                    <button className="admin-sidebar-close" onClick={() => setIsOpen(false)}>
                        <FaTimes />
                    </button>
                </div>

                {/* Nav */}
                <nav className="sidebar-nav">
                    {MENU.map(group => {
                        /* Standalone items (no section header) */
                        if (!group.section) {
                            return group.items.map(item => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({ isActive }) =>
                                        `nav-item ${isActive ? 'active' : ''}`
                                    }
                                >
                                    <span className="nav-icon">{item.icon}</span>
                                    <span>{item.label}</span>
                                </NavLink>
                            ));
                        }
                        /* Collapsible group */
                        return (
                            <NavGroup
                                key={group.id}
                                group={group}
                                isOpen={!!openSections[group.id]}
                                onToggle={() => toggleSection(group.id)}
                            />
                        );
                    })}
                </nav>

                {/* Logout */}
                <div className="sidebar-footer">
                    <button className="logout-btn" onClick={() => setShowLogoutConfirm(true)}>
                        <FaSignOutAlt /> <span>Logout</span>
                    </button>
                </div>
            </div>

            {/* Logout Confirmation Modal */}
            <AnimatePresence>
                {showLogoutConfirm && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="logout-modal"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                        >
                            <div className="modal-icon">
                                <FaExclamationTriangle />
                            </div>
                            <h3>Are you sure?</h3>
                            <p>You are about to sign out of the Admin Portal. Any unsaved changes might be lost.</p>
                            <div className="modal-actions">
                                <button className="modal-btn-cancel" onClick={() => setShowLogoutConfirm(false)}>
                                    Cancel
                                </button>
                                <button className="modal-btn-confirm" onClick={handleLogout}>
                                    Yes, Logout
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Sidebar;
